/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { IAssessmentsConfig } from 'app/core/models/essette-interfaces';
import { apiCall } from 'app/shared/lib/api/api';
import { IApiActionResults, IQueryLoadOptions, IApiQueryResults } from 'app/shared/lib/api/api-types';
import { AssessmentService } from 'app/core/services/assessment-service';
import { McgInformedService } from 'app/ext/mcg-informed/services/mcg-informed-service';
import { localSort } from 'app/shared/lib/sorting';
import { CaseService } from 'app/cm/services/case-service';
import { getReference } from 'app/shared/models/essette-types';
import { Toaster } from 'app/shared/lib/toaster';
import { allActionsSucceeded } from 'app/shared/lib/essette-api';
import * as Core from 'app/core/models/essette-api';
import * as CM from 'app/cm/models/essette-api';
import * as Mcg from 'app/ext/mcg-informed/interfaces';
import { guid } from 'app/shared/lib/guid';

interface IExternalAssessmentAccess {
    cci: boolean;
}

@autoinject
export class AddAttachments extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////

    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected householdCaseId: string | null = null;

    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    public config: IAssessmentsConfig | null = null;

    public existingAttachments: CM.CaseAssessmentAttachment[] = [];

    protected cases: CM.Case[] = [];
    protected selectedCases: CM.Case[] = [];
    protected localSorting = new Sorting();

    @bindable.string
    protected mcgCopyright: string = '';

    protected assessments: CM.CaseAssessment[] = [];
    protected selectedAssessments: CM.CaseAssessment[] = [];

    protected mcgAssessments: CM.CaseAssessment[] = [];
    protected selectedMcgAssessments: CM.CaseAssessment[] = [];
    protected hasMcgAssessments: boolean = false;

    protected externalAssessmentAccess: IExternalAssessmentAccess | null = null;

    protected id: guid = guid();

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly assessmentService: AssessmentService,
        private readonly caseService: CaseService,
        private readonly mcgService: McgInformedService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
        this.checkPermissions();
    }

    protected bind(): void {
        if(!this.householdCaseId) { throw new Error('Bindable property [household-case-id] was not set'); }
        if(!this.config) { throw new Error('Bindable property [config] was not set'); }

        this.assessmentService.configure(this.config);
        this.reload();
    }

    ////****************** Functions ************************************************////

    private async reload(): Promise<void> {
        await Promise.all([
            this.loadLookups(),
            this.loadExistingAttachments()
        ]);
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.externalAssessmentAccess = {
            cci: this.env.config.integrations.includes('cci') && this.accountContext.hasRouteAccess('cci.assessment')
        } as IExternalAssessmentAccess;
    }

    /** Takes an assessment from the selectedAssessments array and constructs a new copy to be sent to the API */
    protected buildAttachment(memberCase: CM.Case, assessment: CM.CaseAssessment): CM.CaseAssessmentAttachment {
        const saveAssessment: CM.CaseAssessmentAttachment = {
            assessment: {
                id: assessment.id,
                version: {
                    id: assessment.currentVersion && assessment.currentVersion.id || null
                }
            },
            case: getReference(memberCase)!
        };

        return saveAssessment;
    }

    ////****************** IForm ****************************************************////

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        return await this.addAssessments();
    }

    public reset(): void {
        this.selectedCases.splice(0);
        this.selectedAssessments.splice(0);
        this.selectedMcgAssessments.splice(0);
        if(this.householdCaseId) {
            this.reload();
        }
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    public async loadLookups(): Promise<void> {
        if(!this.householdCaseId) return;

        const options: Partial<IQueryLoadOptions> = { all: true };
        const assessmentIncludes: string[] = ['externalSystem'];
        const caseIncludes: string[] = ['member'];

        try {
            this.state.beginLoad();

            const [assessments, cases] = await Promise.all(
                [
                    this.assessmentService.loadAssessments(options, assessmentIncludes),
                    this.caseService.loadCasesByHouseholdCase(this.householdCaseId, options, caseIncludes)
                ]
            )

            this.cases = cases.results;
            // use local sorting to sort cases by member description
            this.localSorting.entries = [
                {
                    reference: 'member.description',
                    direction: SortDirection.Ascending
                }
            ];
            localSort(this.cases, this.localSorting);

            if(assessments.results && assessments.results.length > 0) {
                // Filter out assessments that are not activated (no current version)
                const activeAssessments: CM.CaseAssessment[] = assessments.results.filter(a => !!a.currentVersion);

                // Filter out external system assessments
                this.assessments = activeAssessments.filter(a => a.externalSystem === null);

                // Filter for MCG Assessments
                this.mcgAssessments = activeAssessments.filter(a => a.externalSystem?.code == 'CCI');
                this.hasMcgAssessments = this.mcgAssessments.length > 0;

            }
        } catch(error) {
            this.logger.error('Error Loading Lookups', error);
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async loadExistingAttachments(): Promise<void> {
        if(!this.householdCaseId) return;

        const options: Partial<IQueryLoadOptions> = { all: true };

        try {
            this.state.beginLoad();
            const query = this.assessmentService.createAttachmentsByTargetQuery(this.householdCaseId, options, []);
            query.filterOr((f) => {
                f.filterIsNull('task.id');
                f.filterBoolean('task.isActive', true);
            });
            const response = await this.assessmentService.get<Core.AssessmentAttachment>(query);
            this.existingAttachments = response.results;
        } catch(error) {
            this.logger.error('Error Loading Existing Attachments', error);
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async addAssessments(): Promise<boolean> {
        const insertAttachments: CM.CaseAssessmentAttachment[] = [];

        // create a CM.CaseAssessmentAttachment for each valid case/assessment combination
        this.selectedCases.forEach(c => {
            this.selectedAssessments.forEach(a => {
                // if incomplete assessment already attached to case, do not prepare it for API insert
                const existingAttachment = this.existingAttachments.find(ea => ea.case?.id === c.id && ea.assessment?.id === a.id && !ea.isCompleted);
                if(!existingAttachment) {
                    insertAttachments.push(this.buildAttachment(c, a));
                }
            })
        });

        let success = false;
        let mcgSuccess = true;
        try {
            this.state.beginSave();
            if(insertAttachments.length === 0) {
                success = true; // nothing to insert, success!
            } else {
                const progressToast = this.toaster.standard.batch('am:assessment.$', insertAttachments.length);
                progressToast.show();

                const responses = await this.assessmentService.insertAttachmentBatch(insertAttachments, progressToast);
                success = allActionsSucceeded(responses);
            }
            if(success) {
                if(this.selectedMcgAssessments.length > 0) mcgSuccess = await this.addMcgAttachments();
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }

        return success && mcgSuccess;
    }

    @apiCall
    private async loadAttachments(attachmentIds: string[]): Promise<Core.AssessmentAttachment[]> {
        const promises = new Array<Promise<IApiQueryResults<Core.AssessmentAttachment>>>();
        const includes = ['assessment'];
        attachmentIds.forEach(id => { promises.push(this.assessmentService.loadAttachment(id, includes)); });

        try {
            this.state.beginLoad();

            const responses = await Promise.all(promises);
            if(!responses.every(r => r.isSuccessful)) return [];

            const attachments: Core.AssessmentAttachment[] = [];
            responses.forEach(r => {
                attachments.push(...r.results);
            });

            return attachments;
        }
        catch(error) {
            this.toaster.standard.added('am:assessment.$', false, promises.length);
            this.logger.error('Error loading attachments');
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    ////****************** MCG Service Calls ********************************************////
    @apiCall
    private async addMcgAttachments(): Promise<boolean> {
        const insertMcgAttachments: CM.CaseAssessmentAttachment[] = [];

        this.selectedCases.forEach(c => {
            this.selectedMcgAssessments.forEach(a => {
                // check if case/assessment combination exists and if that attachment is complete
                const existingAttachment = this.existingAttachments.find(ea => ea.case?.id === c.id && ea.assessment?.id === a.id && !ea.isCompleted);
                // if attachment doesn't exist, ready it for API insert
                if(!existingAttachment) {
                    insertMcgAttachments.push(this.buildAttachment(c, a));
                }
            })
        });

        let mcgSuccess = false;
        try {
            this.state.beginSave();
            if(insertMcgAttachments.length === 0) {
                return true; // nothing to insert
            }

            const progressToast = this.toaster.standard.batch('am:assessment.$', insertMcgAttachments.length);
            progressToast.show();

            const responses = await this.assessmentService.insertAttachmentBatch(insertMcgAttachments, progressToast);
            mcgSuccess = allActionsSucceeded(responses);
            if(!mcgSuccess) return false;

            const attachmentIds: string[] = [];
            for(const r of responses) {
                if(!r.id) continue;
                attachmentIds.push(r.id);
            }

            const attachments: Core.AssessmentAttachment[] = await this.loadAttachments(attachmentIds);
            const mcgAssessments: Mcg.Assessment[] = await this.loadMcgAssessments(attachments);
            const externalContentSuccess: boolean = await this.addMcgAttachmentExternalContent(attachments, mcgAssessments);

            return mcgSuccess && externalContentSuccess;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async loadMcgAssessments(attachments: Core.AssessmentAttachment[]): Promise<Mcg.Assessment[]> {
        const promises = new Array<Promise<IApiActionResults>>();

        attachments.forEach(a => {
            if(!a.id) return;
            promises.push(this.mcgService.getAssessmentContent(a.id));
        });

        try {
            this.state.beginLoad();

            const responses = await Promise.all(promises);
            if(!responses.every(r => r.isSuccessful)) return [];

            const assessments: Mcg.Assessment[] = [];
            responses.forEach(r => {
                assessments.push(r.result);
            });

            return assessments;
        }
        catch(error) {
            this.toaster.standard.added('am:assessment.$', false, promises.length);
            this.logger.error('Error adding assessments');
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async addMcgAttachmentExternalContent(attachments: Core.AssessmentAttachment[], mcgAssessments: Mcg.Assessment[]): Promise<boolean> {
        const promises = new Array<Promise<IApiActionResults>>();

        try {
            attachments.forEach(a => {
                const assessment: Mcg.Assessment | undefined = mcgAssessments.find(mcg => mcg.hsim === a.assessment?.externalAssessmentIdentifier);
                if(!assessment) return;

                const externalContent: Core.AssessmentAttachmentExternalContent = {
                    assessmentAttachment: { id: a.id },
                    externalContentType: { code: 'AssessmentJson' },
                    content: JSON.stringify(assessment),
                    mediaType: { code: 'data.json' }
                };

                promises.push(this.assessmentService.insertAttachmentExternalContent(externalContent));
            });

            this.state.beginSave();

            const responses = await Promise.all(promises);
            if(!responses.every(r => r.isSuccessful)) return false;

            return true;
        }
        catch(error) {
            this.toaster.standard.added('am:assessment.$', false, promises.length);
            this.logger.error('Error adding assessments');
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

}
